import { useMsal } from '@azure/msal-react';
import { RootState } from 'app/store';
import { appInsights } from 'global/Application/AppInsight';
import { loadSnapshotAction } from "global/Application/dataActions";
import { trimGUIDLower } from 'global/Utility/TSFunctions';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDataSource } from 'global/DataSources/AppDataSource';
import { MsalTokenSource } from 'global/DataSources/MsalTokenSource';

interface UseLoadSnapshotParams {
    accountId?: string;
    snapshotId?: string;
}

/**
 * Custom hook to handle snapshot loading logic with state management
 * Consolidates complex reload conditions and data source initialization
 */
export const useLoadSnapshot = ({ accountId, snapshotId }: UseLoadSnapshotParams) => {
    const dispatch = useDispatch();
    const state = useSelector((state: RootState) => state);
    const { instance } = useMsal();

    // Memoize data sources to prevent unnecessary recreations
    const dataSource = useMemo(() => {
        const tokenSource = new MsalTokenSource(instance);
        return new AppDataSource(tokenSource);
    }, [instance]);

    // Determine if snapshot needs to be reloaded
    const shouldReloadSnapshot = useMemo(() => {
        if (!accountId || !snapshotId) return false;

        // No snapshot loaded yet
        if (!state.status.snapshotLoading && !state?.data?.snapshot?.bcbs_groupconfigurationsnapshotid) {
            return true;
        }

        // Snapshot ID mismatch
        if (state.status.account &&
            state.data?.snapshot?.bcbs_groupconfigurationsnapshotid &&
            trimGUIDLower(state.data.snapshot.bcbs_groupconfigurationsnapshotid!) !== trimGUIDLower(snapshotId)
        ) {
            return true;
        }

        // Account ID mismatch
        if (state.status.account &&
            state.data.snapshot?.bcbs_groupconfigurationsnapshotid &&
            trimGUIDLower(state.status.account?.accountid!) !== trimGUIDLower(accountId)
        ) {
            return true;
        }

        return false;
    }, [accountId, snapshotId, state.status.snapshotLoading, state.status.account, state.data?.snapshot?.bcbs_groupconfigurationsnapshotid, state.data?.account?.accountid]);

    useEffect(() => {
        if (!accountId || !snapshotId) {
            console.error("Unexpected flow, snapshotId or accountId was expected.");
            return;
        }

        if (!shouldReloadSnapshot) return;

        appInsights?.trackEvent({
            name: 'useLoadSnapshot.loadSnapshot',
            properties: {
                snapshotId: snapshotId,
                accountId: accountId,
                stateSnapshotId: state?.data?.snapshot?.bcbs_groupconfigurationsnapshotid,
                stateAccountId: state?.data?.account?.accountid,
            }
        });

        dispatch(loadSnapshotAction(
            dataSource,
            accountId,
            {
                primarySnapshotId: snapshotId,
                purgeStore: true
            }
        ));
    }, [accountId, snapshotId, shouldReloadSnapshot, dispatch, dataSource, state]);

    return { dataSource, isLoading: state.status.snapshotLoading };
};
